import { Delete, Notifications, NotificationsOff } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, IconButton, Table, TableBody, TableCell, TableRow, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import axios from 'axios';
import i18next from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { postToEndpointWithToken } from '../AxiosHelper';
import { DeviceDescriptor } from '../Device';
import { coordinateFormatter, getDeviceStateForDeviceId, getDeviceStatusColor, getLocalDateStringFromUtcDateString, getOrderedOldDeviceDescriptors, getOrderedRecentDeviceDescriptors, isEmailAddressValid } from '../Helper';
import { MonitoringSystemDescriptor, MonitoringSystemState } from '../MonitoringSystem';
import { User } from '../User';
import { AccordionWithTextFieldAndButton } from '../components/AccordionWithTextFieldAndButton';
import InfoIcon from '../components/InfoIcon';
import { useStyles } from '../styles';
import BatteryIcon from './BatteryIcon';
import EditableText from './EditableText';
import './MonitoringSystemPage.css';

interface MonitoringSystemPageProps {
  token: string,
  user: User,
  selectedMonitoringSystemDescriptor: MonitoringSystemDescriptor,
  selectedMonitoringSystemState: MonitoringSystemState,
  updateMonitoringSystemDescriptorRequest: () => void,
  onLogout: () => void
}

function MonitoringSystemPage(props: MonitoringSystemPageProps): JSX.Element {
  const classes = useStyles(useTheme());
  const { token, user, selectedMonitoringSystemDescriptor, selectedMonitoringSystemState, updateMonitoringSystemDescriptorRequest, onLogout } = props;
  const { t } = useTranslation();
  const location = useLocation();
  const addingCodeFromUrl = new URLSearchParams(location.search).get('addingCode') ?? new URLSearchParams(location.state?.from.state?.from.search).get('addingCode');
  const addDeviceInputRef = useRef<HTMLInputElement | null>(null);

  const [monitoringDaysExpanded, setMonitoringDaysExpanded] = useState<boolean>(false);
  const [devicesExpanded, setDevicesExpanded] = useState<boolean>(true);//open by default
  const [guestsExpanded, setGuestsExpanded] = useState<boolean>(false);
  const [deviceExpandedIndex, setDeviceExpandedIndex] = useState<number | undefined>(undefined);

  const [isSendDeviceAccordionExpanded, setIsSendDeviceAccordionExpanded] = useState<boolean>(false);
  const [isRemovingGuest, setIsRemovingGuest] = useState<boolean>(false);
  const [deletingDeviceId, setDeletingDeviceId] = useState<number | undefined>(undefined);
  const [showDeleteInfoDialog, setShowDeleteInfoDialog] = useState<boolean>(false);
  const [showDeletedDevices, setShowDeletedDevices] = useState<boolean>(false);
  const [leaveErrorLabel, setLeaveErrorLabel] = useState<string>('');

  //close all the device accordions when their main accordion changes
  useEffect(() => {
    setDeviceExpandedIndex(undefined);
  }, [devicesExpanded]);

  //close send device accordion if the device expanded index changes
  useEffect(() => {
    setIsSendDeviceAccordionExpanded(false);
  }, [deviceExpandedIndex]);

  const orderedGuests = useMemo(() => {
    return selectedMonitoringSystemDescriptor.guests.sort((g1, g2) => g1.email.localeCompare(g2.email));
  }, [selectedMonitoringSystemDescriptor.guests])

  const orderedRecentDeviceDescriptors = useMemo(() => getOrderedRecentDeviceDescriptors(selectedMonitoringSystemDescriptor, selectedMonitoringSystemState), [selectedMonitoringSystemDescriptor, selectedMonitoringSystemState]);
  const orderedOtherDeviceDescriptors = useMemo(() => getOrderedOldDeviceDescriptors(selectedMonitoringSystemDescriptor, selectedMonitoringSystemState), [selectedMonitoringSystemDescriptor, selectedMonitoringSystemState]);
  const isOwner = selectedMonitoringSystemDescriptor.owner.email === user.email;
  const hasDeletedDevices = useMemo(() => [...orderedRecentDeviceDescriptors, ...orderedOtherDeviceDescriptors].some(deviceDescriptor => deviceDescriptor.deleted), [orderedOtherDeviceDescriptors, orderedRecentDeviceDescriptors]);
  const visibleRecentDeviceDescriptors = useMemo(() => showDeletedDevices ? orderedRecentDeviceDescriptors : orderedRecentDeviceDescriptors.filter(deviceDescriptor => !deviceDescriptor.deleted), [orderedRecentDeviceDescriptors, showDeletedDevices]);
  const visibleOtherDeviceDescriptors = useMemo(() => showDeletedDevices ? orderedOtherDeviceDescriptors : orderedOtherDeviceDescriptors.filter(deviceDescriptor => !deviceDescriptor.deleted), [orderedOtherDeviceDescriptors, showDeletedDevices]);
  const visibleDeviceCount = visibleRecentDeviceDescriptors.length + visibleOtherDeviceDescriptors.length;

  useEffect(() => {
    if (!hasDeletedDevices) {
      setShowDeletedDevices(false);
    }
  }, [hasDeletedDevices]);

  const handleTopupClick = useCallback((topupCode: string): Promise<void> => {
    return postToEndpointWithToken('topupMonitoringSystem', token, { code: topupCode })
      .then(() => {
        updateMonitoringSystemDescriptorRequest();
      })
      .catch((error) => {
        const response = error.response;
        let errorLabel = 'monitoring_system_page.topup.fail.unknown_reason';
        if (axios.isAxiosError(error)) {
          if (response?.status === 401 || response?.status === 403) {
            onLogout();
          } else if (response?.status === 404 || response?.status === 412) {
            errorLabel = 'monitoring_system_page.topup.fail.bad_code';
          } else if (response?.status === 409) {
            errorLabel = 'monitoring_system_page.topup.fail.used_code';
          }
        }
        return Promise.reject(t(errorLabel));
      })
  }, [onLogout, t, token, updateMonitoringSystemDescriptorRequest])

  const handleAddDeviceClick = useCallback((addingCode: string): Promise<void> => {
    return postToEndpointWithToken('addDevice', token, { addingCode: addingCode })
      .then(() => {
        updateMonitoringSystemDescriptorRequest();
      })
      .catch((error) => {
        const response = error.response;
        let errorLabel = 'monitoring_system_page.add_device.fail.unknown_reason';
        if (axios.isAxiosError(error)) {
          if (response?.status === 401 || response?.status === 403) {
            onLogout();
          } else if (response?.status === 404) {
            errorLabel = 'monitoring_system_page.add_device.fail.bad_code';
          } else if (response?.status === 409) {
            errorLabel = 'monitoring_system_page.add_device.fail.used_code';
          }
        }
        return Promise.reject(t(errorLabel));
      })
  }, [onLogout, t, token, updateMonitoringSystemDescriptorRequest])

  const handleDeleteDeviceClick = useCallback((deviceId: number) => {
    setDeletingDeviceId(deviceId);
    postToEndpointWithToken('deleteDevice', token, { deviceId: deviceId })
      .then(() => {
        setDeviceExpandedIndex(undefined);
        setShowDeleteInfoDialog(true);
        updateMonitoringSystemDescriptorRequest();
        setDeletingDeviceId(undefined);
      })
      .catch((error) => {
        const response = error.response;
        if (axios.isAxiosError(error) && (response?.status === 401 || response?.status === 403)) {
          onLogout();
        } else {
          console.error('Deleting device failed with status:', response?.status);
        }
        setDeletingDeviceId(undefined);
      })
  }, [onLogout, token, updateMonitoringSystemDescriptorRequest])

  const handleRestoreDeviceClick = useCallback((deviceId: number) => {
    setDeletingDeviceId(deviceId);
    postToEndpointWithToken('restoreDevice', token, { deviceId: deviceId })
      .then(() => {
        updateMonitoringSystemDescriptorRequest();
        setDeletingDeviceId(undefined);
      })
      .catch((error) => {
        const response = error.response;
        if (axios.isAxiosError(error) && (response?.status === 401 || response?.status === 403)) {
          onLogout();
        } else {
          console.error('Restoring device failed with status:', response?.status);
        }
        setDeletingDeviceId(undefined);
      })
  }, [onLogout, token, updateMonitoringSystemDescriptorRequest])

  const handleAddGuestClick = useCallback((newGuestEmail: string): Promise<void> => {
    return postToEndpointWithToken('inviteGuest', token, { email: newGuestEmail })
      .then(() => {
        updateMonitoringSystemDescriptorRequest();
      })
      .catch((error) => {
        const response = error.response;
        let errorLabel = 'monitoring_system_page.guests.fail.unknown_reason';
        if (axios.isAxiosError(error)) {
          if (response?.status === 401 || response?.status === 403) {
            onLogout();
          } else if (response?.status === 404) {
            errorLabel = 'monitoring_system_page.guests.fail.not_exist';
          } else if (response?.status === 409) {
            errorLabel = 'monitoring_system_page.guests.fail.already_added';
          }
        }
        return Promise.reject(t(errorLabel));
      })
  }, [onLogout, t, token, updateMonitoringSystemDescriptorRequest])

  const handleRemoveGuestClick = useCallback((email: string) => {
    setIsRemovingGuest(true);
    postToEndpointWithToken('removeGuest', token, { email: email })
      .then(() => {
        updateMonitoringSystemDescriptorRequest();
        setIsRemovingGuest(false);
      })
      .catch((error) => {
        const response = error.response;
        let errorLabel = 'monitoring_system_page.guests.fail.remove';
        if (axios.isAxiosError(error)) {
          if (response?.status === 401 || response?.status === 403) {
            onLogout();
          }
        }
        setIsRemovingGuest(false);
        console.error(errorLabel); //TODO find a better way to show this error to the user
      })
  }, [onLogout, token, updateMonitoringSystemDescriptorRequest]);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);

  const onLeave = useCallback(() => {
    postToEndpointWithToken('leaveMonitoringSystem', token, { monitoringSystemId: selectedMonitoringSystemDescriptor.monitoringSystemId })
      .then(() => {
        setLeaveErrorLabel('');
        updateMonitoringSystemDescriptorRequest();
      })
      .catch((error) => {
        const response = error.response;
        let errorLabel = 'monitoring_system_page.guests.fail.leave';
        if (axios.isAxiosError(error)) {
          if (response?.status === 401 || response?.status === 403) {
            onLogout();
          }
        }
        setLeaveErrorLabel(errorLabel);
      })
  }, [onLogout, selectedMonitoringSystemDescriptor.monitoringSystemId, token, updateMonitoringSystemDescriptorRequest]);

  //close the expanded item when changing between monitoring systems (but do not close when only a property of the same monitoring system changes)
  useEffect(() => {
    if (selectedMonitoringSystemDescriptor?.monitoringSystemId !== undefined) {
      setDeviceExpandedIndex(undefined);
    }
  }, [selectedMonitoringSystemDescriptor?.monitoringSystemId])

  useEffect(() => {
    if (addingCodeFromUrl !== null) {
      setTimeout(() => {
        if (addDeviceInputRef.current) {
          addDeviceInputRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 200); // Timeout needed for the accordion to open and the stuff to be rendered
    }
  }, [addingCodeFromUrl])

  const handleDeviceRename = useCallback((index: number, newName: string) => {
    const deviceId = index > orderedRecentDeviceDescriptors.length - 1 ? orderedOtherDeviceDescriptors[index - orderedRecentDeviceDescriptors.length].deviceId : orderedRecentDeviceDescriptors[index].deviceId;
    postToEndpointWithToken('renameDevice', token, { deviceId: deviceId, name: newName })
      .then(() => {
        updateMonitoringSystemDescriptorRequest();
      })
      .catch((error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          onLogout();
        } else {
          console.error("Unknown error occured with status:", error.response?.status);
        }
      })
  }, [onLogout, orderedOtherDeviceDescriptors, orderedRecentDeviceDescriptors, token, updateMonitoringSystemDescriptorRequest]);

  const handleIgnoreClick = useCallback((deviceId: number, toIgnore: boolean) => () => {
    postToEndpointWithToken('ignoreDevice', token, { devicesToIgnore: [{ deviceId: deviceId, toIgnore: toIgnore }] })
      .then(() => {
        updateMonitoringSystemDescriptorRequest();
      })
      .catch((error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          onLogout();
        } else {
          console.error("Unknown error occured with status:", error.response?.status);
        }
      })
  }, [onLogout, token, updateMonitoringSystemDescriptorRequest]);

  const handleSendDeviceClick = useCallback((email: string, deviceId: number): Promise<void> => {
    return postToEndpointWithToken('sendDevice', token, { email: email, deviceId: deviceId })
      .then(() => {
        updateMonitoringSystemDescriptorRequest();
      })
      .catch((error) => {
        const response = error.response;
        if (axios.isAxiosError(error)) {
          if (response?.status === 401 || response?.status === 403) {
            onLogout();
          } else if (response?.status === 404) {
            //it is already accepted, we just make the button disappear
            updateMonitoringSystemDescriptorRequest();
            console.error(t('monitoring_system_page.send_device.fail.not_exist'));//TODO find a better way to show this to the user
          }
        }
      })
  }, [onLogout, t, token, updateMonitoringSystemDescriptorRequest])

  const handleAcceptClick = useCallback((deviceId: number): Promise<void> => {
    return postToEndpointWithToken('acceptDevice', token, { deviceId: deviceId })
      .then(() => {
        updateMonitoringSystemDescriptorRequest();
      })
      .catch((error) => {
        const response = error.response;
        if (axios.isAxiosError(error)) {
          if (response?.status === 401 || response?.status === 403) {
            onLogout();
          } else if (response?.status === 404) {
            //it is already cancelled, we just make the button disappear
            updateMonitoringSystemDescriptorRequest();
            console.error(t('monitoring_system_page.accept_device.fail.not_exist'));//TODO find a better way to show this to the user
          }
        }
      })
  }, [onLogout, t, token, updateMonitoringSystemDescriptorRequest])

  const handleCancelSendClick = useCallback((deviceId: number): Promise<void> => {
    return postToEndpointWithToken('cancelDeviceSend', token, { deviceId: deviceId })
      .then(() => {
        updateMonitoringSystemDescriptorRequest();
      })
      .catch((error) => {
        const response = error.response;
        if (axios.isAxiosError(error)) {
          if (response?.status === 401 || response?.status === 403) {
            onLogout();
          } else if (response?.status === 404) {
            //it is already accepted, we just make the button disappear
            updateMonitoringSystemDescriptorRequest();
            console.error(t('monitoring_system_page.cancel_send_device.fail.not_exist'));//TODO find a better way to show this to the user
          }
        }
      })
  }, [onLogout, t, token, updateMonitoringSystemDescriptorRequest])


  const BUDGET_WARN_DAYS = 14;
  const budgetDaysLeft = selectedMonitoringSystemState.monitoringDaysBudget / Math.max(orderedRecentDeviceDescriptors.length, 1);
  const budgetColor = selectedMonitoringSystemState.monitoringDaysBudget <= 0 ? "#d32f2f" : (budgetDaysLeft <= BUDGET_WARN_DAYS ? "#ff9800" : "rgba(0, 0, 0, 0.87)");

  const getDeviceGridItems = useCallback((deviceDescriptors: DeviceDescriptor[], startIndex: number) => {
    return deviceDescriptors.map((deviceDescriptor, index) => {
      const deviceState = getDeviceStateForDeviceId(deviceDescriptor.deviceId, selectedMonitoringSystemState);
      const idx = index + startIndex;
      return <Grid key={deviceDescriptor.deviceId} size='grow'>
        {deviceDescriptor.sentTo === user.email ?
          <Button
            onClick={() => handleAcceptClick(deviceDescriptor.deviceId)}
            variant="contained"
            color="primary"
            fullWidth
          >
            {t('monitoring_system_page.accept_device.btn', { deviceId: deviceDescriptor.deviceId })}
          </Button> :
          deviceDescriptor.sentTo !== null && deviceDescriptor.sentTo !== '' ?
            <Button
              onClick={() => handleCancelSendClick(deviceDescriptor.deviceId)}
              variant="contained"
              color="primary"
              fullWidth
            >
              {t('monitoring_system_page.cancel_send_device.btn', { deviceId: deviceDescriptor.deviceId })}
            </Button> :
            <Accordion
              expanded={deviceExpandedIndex === idx}
              sx={{
                id: deviceDescriptor.deviceId,
                width: '328px' //width is measured back from the screen... quick and dirty yolo
              }}
              onChange={() => setDeviceExpandedIndex(prev => prev === undefined ? idx : prev === idx ? undefined : idx)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{ backgroundColor: deviceDescriptor.isIgnored ? 'lightgray' : undefined }}>
                <Grid container width='100%' alignItems='center' justifyContent='center' spacing={1}>
                  <Grid size='auto' >
                    {deviceDescriptor.isIgnored ? <NotificationsOff /> : (deviceState !== undefined && <span className={"circle " + getDeviceStatusColor(deviceState.status) + "-circle"}></span>)}
                  </Grid>
                  <Grid size='auto' >
                    {!deviceDescriptor.isIgnored ? (deviceState !== undefined && BatteryIcon(deviceState.battery)) : <InfoIcon infoLabel="device.ignored_title" />}
                  </Grid>
                  <Grid size='grow' >
                    {selectedMonitoringSystemDescriptor.owner.email === user.email ?
                      <EditableText value={deviceDescriptor.name} onSave={(newName) => handleDeviceRename(idx, newName)} /> :
                      <Typography>{deviceDescriptor.name}</Typography>}
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails
                style={{ backgroundColor: deviceDescriptor.isIgnored ? 'lightgray' : undefined }}
              >
                {deviceExpandedIndex === idx && <Table size='small'>
                  <TableBody>
                    <TableRow>
                      <TableCell variant="body" width={"60%"}>{t('device.serial')}</TableCell>
                      <TableCell variant="body">{deviceDescriptor.deviceId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="body">{t('device.status.title')}</TableCell>
                      <TableCell variant="body">{t('device.status.' + deviceState?.status.toLowerCase())}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="body">{t('device.location.title')}</TableCell>
                      <TableCell variant="body">
                        {deviceState?.latitude === null || deviceState?.latitude === undefined ? t('device.location.no_location') :
                          <>
                            {t('device.location.latitude', { latitude: coordinateFormatter(i18next.language).format(deviceState.latitude) })}
                            <br />
                            {t('device.location.longitude', { longitude: coordinateFormatter(i18next.language).format(deviceState.longitude!) })}
                            <br />
                            {!deviceDescriptor.isIgnored && <Link to={`/map?deviceId=${deviceState?.deviceId}`}>
                              {t('device.go_to_map')}
                            </Link>}
                          </>
                        }
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="body">{t('device.battery')}</TableCell>
                      <TableCell variant="body">{deviceState?.battery}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="body">{t('device.temperature')}</TableCell>
                      <TableCell variant="body">{deviceState?.temperature}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="body">{t('device.signal_strength')}</TableCell>
                      <TableCell variant="body">{deviceState?.signalStrength}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="body">{t('device.last_seen')}</TableCell>
                      <TableCell variant="body">{deviceState === undefined ? undefined : getLocalDateStringFromUtcDateString(deviceState.lastSeen, i18next.language)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="body">{t(deviceDescriptor.isIgnored ? 'device.ignored' : 'device.not_ignored')}</TableCell>
                      <TableCell variant="body">{
                        <IconButton
                          title={deviceDescriptor.isIgnored ? t('device.unignore') : t('device.ignore')}
                          onClick={handleIgnoreClick(deviceDescriptor.deviceId, !deviceDescriptor.isIgnored)}
                        >
                          {deviceDescriptor.isIgnored ? <NotificationsOff /> : <Notifications />}
                        </IconButton>
                      }</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant="body" colSpan={2}>
                        <AccordionWithTextFieldAndButton
                          disabled={false}
                          buttonText={t('monitoring_system_page.send_device.btn')}
                          buttonTooltip={t('monitoring_system_page.send_device.btn_tooltip')}
                          expanded={isSendDeviceAccordionExpanded}
                          onExpandedChange={() => setIsSendDeviceAccordionExpanded(prev => !prev)}
                          onClick={(email) => handleSendDeviceClick(email, deviceDescriptor.deviceId)}
                          validateText={(email) => email === '' || isEmailAddressValid(email) ? (email === user.email ? t('monitoring_system_page.send_device.fail.self') : '') : t('monitoring_system_page.send_device.fail.invalid_email')}
                          summaryCounterTooltip={""}
                          summaryTextLeft={t('monitoring_system_page.send_device.title')}
                          textfieldEmptyText={t('monitoring_system_page.send_device.input') + ' *'}
                        />
                      </TableCell>
                    </TableRow>
                    {isOwner && <TableRow>
                      <TableCell variant="body" colSpan={2} align='center'>
                        <Button
                          variant={"contained"}
                          color={deviceDescriptor.deleted ? "primary" : "error"}
                          onClick={() => deviceDescriptor.deleted ? handleRestoreDeviceClick(deviceDescriptor.deviceId) : handleDeleteDeviceClick(deviceDescriptor.deviceId)}
                          disabled={deletingDeviceId !== undefined}
                        >
                          {deletingDeviceId === deviceDescriptor.deviceId ? <CircularProgress size={24} /> : (deviceDescriptor.deleted ? t('monitoring_system_page.restore_device.btn') : t('monitoring_system_page.delete_device.btn'))}
                        </Button>
                      </TableCell>
                    </TableRow>}
                  </TableBody>
                </Table>}
              </AccordionDetails>
            </Accordion>}
      </Grid>
    });
  }, [deletingDeviceId, deviceExpandedIndex, handleAcceptClick, handleCancelSendClick, handleDeleteDeviceClick, handleDeviceRename, handleIgnoreClick, handleRestoreDeviceClick, handleSendDeviceClick, isOwner, isSendDeviceAccordionExpanded, selectedMonitoringSystemDescriptor.owner.email, selectedMonitoringSystemState, t, user.email]);


  const orderedRecentDeviceGridItems = useMemo(() => getDeviceGridItems(visibleRecentDeviceDescriptors, 0),
    [visibleRecentDeviceDescriptors, getDeviceGridItems]);
  const orderedOtherDeviceGridItems = useMemo(() => getDeviceGridItems(visibleOtherDeviceDescriptors, visibleRecentDeviceDescriptors.length),
    [visibleOtherDeviceDescriptors, getDeviceGridItems, visibleRecentDeviceDescriptors.length]);

  return <FormControl className={classes.mainFrame}>
    <Typography variant='h5' className={classes.title}>
      <Trans>{selectedMonitoringSystemDescriptor.name}</Trans>
    </Typography>
    <Grid container direction='column' width={'380px'}>
      <Grid style={{ marginTop: 10 }}>
        <AccordionWithTextFieldAndButton
          disabled={false}
          summaryCounterTooltip={budgetDaysLeft <= 0 ? t('monitoring_system_page.topup.no_budget_tooltip') : budgetDaysLeft <= BUDGET_WARN_DAYS ? t('monitoring_system_page.topup.warn_budget_tooltip', { daysLeft: budgetDaysLeft }) : ""}
          summaryTextLeft={t('monitoring_system_page.topup.budget')}
          summaryCounter={selectedMonitoringSystemState.monitoringDaysBudget}
          summaryCounterColor={budgetColor}
          expanded={monitoringDaysExpanded}
          onExpandedChange={() => setMonitoringDaysExpanded(prev => { return !prev; })}
          onClick={handleTopupClick}
          textfieldEmptyText={t('monitoring_system_page.topup.input') + ' *'}
          buttonText={t('monitoring_system_page.topup.btn')}
          buttonTooltip={t('monitoring_system_page.topup.btn_tooltip')}
          clickableLink='https://shop.feedbackpack.eu/products/100-megfigyelesi-nap'
          clickableLinkText={t('monitoring_system_page.topup.webshop_link')}
        />
      </Grid>
      <Grid>
        <AccordionWithTextFieldAndButton
          inputRef={addDeviceInputRef}
          disabled={false}
          summaryCounterTooltip=''
          summaryTextLeft={t('monitoring_system_page.devices_title')}
          summaryCounter={visibleDeviceCount}
          expanded={devicesExpanded}
          onExpandedChange={() => setDevicesExpanded(prev => !prev)}
          onClick={handleAddDeviceClick}
          textfieldEmptyText={t('monitoring_system_page.add_device.input_help') + ' *'}
          buttonText={t('monitoring_system_page.add_device.btn')}
          buttonTooltip={t('monitoring_system_page.add_device.btn_tooltip')}
          clickableLink='https://shop.feedbackpack.eu/products/solo-sam-vadcsapda-megfigyelo-120-ajandek-megfigyelesi-nappal'
          textFieldInitText={addingCodeFromUrl ?? undefined}
          clickableLinkText={t('monitoring_system_page.add_device.webshop_link')}
          childrenBelowButton={isOwner && hasDeletedDevices ? <Grid style={{ width: '100%' }}>
            <FormControlLabel
              control={<Checkbox checked={showDeletedDevices} onChange={(e) => setShowDeletedDevices(e.target.checked)} />}
              label={t('monitoring_system_page.show_deleted_devices')}
            />
          </Grid> : undefined}
          children={orderedRecentDeviceGridItems.length === 0 ?
            <Grid container spacing={1} direction={'column'}>
              {orderedOtherDeviceGridItems}
            </Grid> : orderedOtherDeviceGridItems.length === 0 ?
              <Grid container spacing={1} direction={'column'}>
                {orderedRecentDeviceGridItems}
              </Grid>
              : <div>
                <Grid container spacing={1} direction={'column'}>
                  <Grid>
                    <Typography align='center' style={{ fontWeight: 'bold' }}>
                      {t('monitoring_system_page.recent_devices_title')}
                    </Typography>
                  </Grid>
                  <Grid container spacing={1} direction={'column'}>
                    {orderedRecentDeviceGridItems}
                  </Grid>
                </Grid>
                <Grid container spacing={1} direction={'column'}>
                  <Grid>
                    <Typography align='center' style={{ fontWeight: 'bold', marginTop: 10 }}>
                      {t('monitoring_system_page.old_devices_title')}
                    </Typography>
                  </Grid>
                  <Grid container spacing={1} direction={'column'}>
                    {orderedOtherDeviceGridItems}
                  </Grid>
                </Grid>
              </div>
          }
        />
      </Grid>
      <Grid>
        {user.email === selectedMonitoringSystemDescriptor.owner.email ?
          <AccordionWithTextFieldAndButton
            disabled={false}
            summaryCounterTooltip={t('monitoring_system_page.guests.tooltip')}
            summaryTextLeft={t('monitoring_system_page.guests.title')}
            summaryCounter={selectedMonitoringSystemDescriptor.guests.length}
            expanded={guestsExpanded}
            onExpandedChange={() => setGuestsExpanded(prev => !prev)}
            onClick={handleAddGuestClick}
            textfieldEmptyText={t('monitoring_system_page.guests.input') + ' *'}
            buttonText={t('monitoring_system_page.guests.btn')}
            buttonTooltip={t('monitoring_system_page.guests.btn_tooltip')}
            validateText={(email) => email === '' || isEmailAddressValid(email) ? (email === user.email ? t('monitoring_system_page.guests.fail.self') : '') : t('monitoring_system_page.guests.fail.invalid_email')}
            children={
              selectedMonitoringSystemDescriptor.guests.length > 0 ?
                <Grid>
                  {
                    orderedGuests.map((guest) => {
                      return <Grid container alignItems='center' justifyContent='space-between'>
                        <Grid>
                          <Typography style={{ marginLeft: 15 }}>{guest.email}</Typography>
                        </Grid>
                        <Grid>
                          <IconButton onClick={() => handleRemoveGuestClick(guest.email)} >
                            {isRemovingGuest ? <CircularProgress /> : <Delete />}
                          </IconButton>
                        </Grid>
                        <Grid>
                          <InfoIcon infoLabel="monitoring_system_page.guests.delete_btn_tooltip" />
                        </Grid>
                      </Grid>
                    })
                  }
                </Grid> : undefined
            }
          />
          :
          <Grid container alignItems='center' justifyContent='center' direction='column'>
            <Grid>
              <Typography style={{ color: 'red' }}>
                {t(leaveErrorLabel)}
              </Typography>
            </Grid>
            <Grid>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                onClick={() => setConfirmDialogOpen(true)}
              >
                {t('monitoring_system_page.guests.leave')}
              </Button>
            </Grid>
          </Grid>
        }
      </Grid>
    </Grid >
    <Dialog open={confirmDialogOpen} >
      <DialogTitle>{t('monitoring_system_page.guests.confirm_leave_title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('monitoring_system_page.guests.confirm_leave_body')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Grid container alignItems='center' justifyContent='flex-start' spacing={1}>
          <Grid >
            <Button
              onClick={() => {
                setConfirmDialogOpen(false);
                onLeave();
              }}
              variant="contained"
              color='secondary'>
              {t('monitoring_system_page.guests.confirm_leave_btn')}
            </Button>
          </Grid>
          <Grid >
            <Button
              onClick={() => setConfirmDialogOpen(false)}
              variant="outlined"
              color="primary"
            >
              {t('monitoring_system_page.guests.confirm_leave_cancel_btn')}
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
    <Dialog open={showDeleteInfoDialog} onClose={() => setShowDeleteInfoDialog(false)}>
      <DialogTitle>{t('monitoring_system_page.delete_device.info_popup_title')}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('monitoring_system_page.delete_device.info_popup_body', {
            showDeletedDevicesLabel: t('monitoring_system_page.show_deleted_devices'),
            restoreDeviceButtonLabel: t('monitoring_system_page.restore_device.btn')
          })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowDeleteInfoDialog(false)} variant="contained" color='primary'>
          {t('install_app.ack_btn')}
        </Button>
      </DialogActions>
    </Dialog>
  </FormControl >
}

export default MonitoringSystemPage;
